import React, { useEffect, useState, useCallback } from "react";
import { debounce } from "lodash";
import { useLazyQuery } from "@apollo/client";

import { LIST_VEHICLES } from "../../../../../../GraphQL/Queries/vehicles";
import { exportToastAsync, generateVehicleReport } from '../../../../../../Helpers/generateCsv';

export const INITIAL_SEARCH_FILTERS = { makes: [], year: [], mileage: {}, status: [], tiers: [] };
export const INITIAL_SORT_FILTERS = { sortBy: null, sortOrder: null };

export const useVehicles = ({ userSession, match, history }) => {
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchFilters, setSearchFilters] = useState(INITIAL_SEARCH_FILTERS);
  const [sortFilters, setSortFilters] = useState(INITIAL_SORT_FILTERS);
  const [vehicleFiltersData, setVehicleFiltersData] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [vehiclesCountTotal, setVehiclesCountTotal] = useState(0);

  const addVehicle = () => {
    history.push(match.url + "/vehicle/New");
  }

  useEffect(() => {
    searchVehicles().then((result) => {
      const { filterAggregations } = result?.data?.listVehicles;

      setVehicles(result?.data?.listVehicles?.vehicles);
      setVehiclesCountTotal(filterAggregations?.vehiclesCount?.total);
      setSearching(false);
      setCursor(result?.data?.listVehicles?.metaData?.nextCursor);

      const filters = {
        makes: filterAggregations?.makes,
        years: filterAggregations?.years,
        stakeholders: filterAggregations?.stakeholderIds,
        vehicleManagementHubs: filterAggregations?.vehicleManagementHubIds,
        tiers: filterAggregations?.tiers,
        mileage: filterAggregations?.odometerRange,
        status: ["active", "inactive"],
      };

      setSearchFilters(INITIAL_SEARCH_FILTERS);
      setVehicleFiltersData(filters);
    });
  }, []);

  const [searchVehicles, { data, loading: searchVehiclesLoading, called, networkStatus }] = useLazyQuery(LIST_VEHICLES, {
    variables: {
      stakeholderAndVehicleCenterInputs: userSession.user?.profile_access?.active_sh_access,
      first: 40,
      filterInput: null,
      vehicleSearchTerm: null,
      after: null,
      orderBy: null,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    setLoading(searchVehiclesLoading);
  }, [searchVehiclesLoading]);

  const parseSearchFilters = () => ({
    vehicleSearchTerm: searchTerm,
    filterInput: {
      makeIn: searchFilters?.makes,
      yearIn: searchFilters?.year,
      odometerGte: searchFilters?.mileage?.min,
      odometerLte: searchFilters?.mileage?.max,
      statusEq: searchFilters?.status[0],
      tierIn: searchFilters?.tiers,
    },
    orderBy: sortFilters?.sortBy ? {
      field: sortFilters?.sortBy,
      order: sortFilters?.sortOrder,
    } : null,
  });

  const getSearchResults = useCallback(
    debounce((vars) => {
      setCursor(null);
      setVehicles([]);
      setVehiclesCountTotal(0);
      searchVehicles({ variables: vars }).then((result) => {
        setVehicles(result?.data?.listVehicles?.vehicles);
        setVehiclesCountTotal(result?.data?.listVehicles?.filterAggregations?.vehiclesCount?.filtered);
        setSearching(false);
        setCursor(result?.data?.listVehicles?.metaData?.nextCursor);
      });
    }, 800),
    [searchFilters, searchTerm]
	);

  useEffect(() => {
    if (vehicleFiltersData) {
      if (searchTerm) setSearching(true);

      if (data) getSearchResults(parseSearchFilters());
    }
  }, [searchTerm, searchFilters]);

  useEffect(() => {
    setLoading(true);
    setCursor(null);
    setVehicles([]);
    setVehiclesCountTotal(0);
    searchVehicles({
      variables: parseSearchFilters(),
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "cache-and-network",
    }).then((result) => {
      setVehicles(result?.data?.listVehicles?.vehicles);
      setVehiclesCountTotal(result?.data?.listVehicles?.filterAggregations?.vehiclesCount?.filtered);
      setSearching(false);
      setCursor(result?.data?.listVehicles?.metaData?.nextCursor);
    });
  }, [sortFilters]);

  const searchOnScroll = (event) => {
    if (data && cursor && !loading) {
      if (event.target && event.target.scrollTop + event.target.clientHeight >= event.target.firstChild?.scrollHeight) {
        setLoading(true);

        const variables = cursor ? { after: cursor, ...parseSearchFilters() } : parseSearchFilters();

        searchVehicles({
          variables,
          notifyOnNetworkStatusChange: true,
          fetchPolicy: "cache-and-network",
        }).then((result) => {
          const newVehicles = [...vehicles, ...result?.data?.listVehicles?.vehicles];
          setVehicles(newVehicles);
          setVehiclesCountTotal(result?.data?.listVehicles?.filterAggregations?.vehiclesCount?.filtered);
          setSearching(false);
          setCursor(result?.data?.listVehicles?.metaData?.nextCursor);
        }).finally(() => {
          setLoading(false);
        });
      }
    }
  };

  const updateSorting = (sortBy) => {
    setSortFilters(currentSort => ({
      sortBy,
      sortOrder: currentSort.sortOrder === "desc" ? "asc" : "desc"
    }));
  }

  const startExport = () => {
      const data =  {
          search_term: searchTerm,
          // makes: searchFilters?.makes, // not sure why it's not working properly maybe due to mixed data from GraphQL and legacy API.
          // sort_filter: this.state.sort_filter,
          // sort_direction: this.state.sort_direction,
          bookings_min: searchFilters.bookings?.min,
          bookings_max: searchFilters.bookings?.max,
          last_booking_min: searchFilters.last_booking?.min,
          last_booking_max: searchFilters.last_booking?.max,
          utilization_min: searchFilters.utilization?.min,
          utilization_max: searchFilters.utilization?.max,
          mileage_min: searchFilters.mileage?.min > 0 ? searchFilters.mileage?.min : undefined,
          mileage_max: searchFilters.mileage?.max < 10000 ? searchFilters.mileage?.max : undefined,
      }

      exportToastAsync({
          type: 'vehicle',
          request: {
              method: 'post',
              url: '/ajax/vehicle_list/report',
              data,
          },
          onResolve: generateVehicleReport,
          onReject: null
      });
  }

  return {
    vehicles,
    searching,
    loading,
    addVehicle,
    vehiclesCountTotal,
    vehicleFiltersData,
    searchFilters,
    sortFilters,
    setSearchFilters,
    searchTerm,
    setSearchTerm,
    searchOnScroll,
    updateSorting,
    startExport,
  };
};
